import { useEffect, useMemo, useState } from "react";
import { CalendarClock, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { listResource } from "@/lib/api";
import { fdate } from "@/lib/format";
import { safeText } from "@/lib/safeText";
import { useAuth } from "@/context/AuthContext";

const SEM = {
  Rojo: { dot: "bg-red-500", border: "border-l-red-500", icon: AlertCircle, color: "text-red-600" },
  Amarillo: { dot: "bg-amber-500", border: "border-l-amber-500", icon: Clock, color: "text-amber-600" },
  Verde: { dot: "bg-emerald-500", border: "border-l-emerald-500", icon: CheckCircle2, color: "text-emerald-600" },
};

// feb-2026 · iter 07-25 · Filtro por régimen según giro del tenant.
// Una inmobiliaria (LFPIORPI Art. 17, supervisor SAT-UIF) NO debe ver obligaciones CNBV /
// CONDUSEF ni de otros regímenes. Se filtra client-side por (regimen, autoridad) sin tocar
// el CRUD genérico `/api/calendario`.
function autoridadAplicaAlGiro(autoridad, giro) {
  if (!autoridad || !giro) return true;
  const a = String(autoridad).toUpperCase();
  if (giro === "inmobiliaria") {
    // Autoridades legítimas de una actividad vulnerable: SAT-UIF, SPPLD, Interno.
    return a.includes("SAT") || a.includes("UIF") || a.includes("SPPLD") || a === "INTERNO";
  }
  if (giro === "sofom") {
    // SOFOM ENR ve todo (CNBV, CONDUSEF, SAT-UIF, Interno).
    return true;
  }
  return true; // giro desconocido: no filtramos (fail-open).
}

function regimenAplicaAlGiro(regimen, giro) {
  if (!regimen || !giro) return true;
  const r = String(regimen).toLowerCase();
  if (giro === "inmobiliaria") {
    return r.includes("inmob") || r.includes("lfpiorpi") || r === "";
  }
  if (giro === "sofom") return true;
  return true;
}

export default function Calendario() {
  const { giro } = useAuth();
  const [items, setItems] = useState([]);
  const [autoridad, setAutoridad] = useState("Todas");
  const [estatus, setEstatus] = useState("Todos");

  useEffect(() => { listResource("calendario").then(setItems); }, []);

  // Filtro base por giro del tenant (aplica ANTES de los filtros manuales del usuario).
  const itemsPorGiro = useMemo(
    () => items.filter((i) => autoridadAplicaAlGiro(i.autoridad, giro) && regimenAplicaAlGiro(i.regimen, giro)),
    [items, giro],
  );

  const autoridades = useMemo(() => ["Todas", ...new Set(itemsPorGiro.map((i) => i.autoridad))], [itemsPorGiro]);

  const filtered = itemsPorGiro.filter((i) =>
    (autoridad === "Todas" || i.autoridad === autoridad) && (estatus === "Todos" || i.estatus === estatus)
  ).sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));

  const counts = {
    proximas: itemsPorGiro.filter((i) => i.estatus === "Próxima").length,
    vencidas: itemsPorGiro.filter((i) => i.estatus === "Vencida").length,
    cumplidas: itemsPorGiro.filter((i) => i.estatus === "Cumplida").length,
  };

  return (
    <div>
      <PageHeader title="Calendario regulatorio" subtitle="Obligaciones próximas, vencidas y cumplidas — semáforo de riesgo" />
      <div className="space-y-6 p-6 md:p-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard label="Obligaciones próximas" value={counts.proximas} icon="Clock" accent="amber" />
          <StatCard label="Obligaciones vencidas" value={counts.vencidas} icon="AlertCircle" accent="red" />
          <StatCard label="Obligaciones cumplidas" value={counts.cumplidas} icon="CheckCircle2" accent="green" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={autoridad} onValueChange={setAutoridad}>
            <SelectTrigger data-testid="cal-filter-autoridad" className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>{autoridades.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={estatus} onValueChange={setEstatus}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>{["Todos", "Próxima", "Vencida", "Cumplida"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {filtered.length === 0 && <p className="text-sm text-slate-400">Sin obligaciones para los filtros seleccionados.</p>}
          {filtered.map((i) => {
            const sem = SEM[i.semaforo] || SEM.Verde;
            const Icon = sem.icon;
            return (
              <div key={i.id} data-testid={`cal-item-${i.id}`}
                className={`flex items-center justify-between rounded-lg border border-slate-200 border-l-4 bg-white p-4 shadow-sm ${sem.border}`}>
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${sem.color}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{i.titulo}</p>
                    <p className="text-xs text-slate-500">{safeText(i.autoridad)} · {safeText(i.regimen)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-slate-700"><CalendarClock className="h-4 w-4 text-slate-400" /> {fdate(i.fecha)}</p>
                  <p className={`text-xs font-medium ${sem.color}`}>{i.estatus}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
